/**
 * EL ADAPTADOR DE LA PERSONA.
 *
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║  ESTE ES EL ÚNICO ARCHIVO QUE CONOCE LA FORMA DEL API DE USUARIOS.        ║
 * ║  El contrato vive en `api/src/routes/users.ts` (+ services/users.ts).     ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 *
 * ─── POR QUÉ ESTE DOMINIO VA PRIMERO ────────────────────────────────────────
 *
 * En Postgres TODO cuelga de `app_user` por clave foránea. Un carro, un pedido,
 * un producto o una cita de alguien que no está en esa tabla no se guardan a
 * medias: la escritura MUERE con `violates foreign key constraint`. Así que la
 * primera pieza no es una pantalla, es el ALTA: sin ella, encender cualquier
 * otro dominio deja al usuario apretando "guardar" contra un error de base.
 *
 * ─── LO QUE EL API NO ACEPTA DEL CUERPO, Y NO ES POCO ───────────────────────
 *
 * El contrato es una LISTA DE PERMITIDOS y responde 400 NOMBRANDO el campo que
 * sobra —no lo ignora—, así que mandar el documento de Firestore tal cual falla
 * entero. Fuera del cuerpo se quedan, a propósito:
 *
 *   · `email`      — sale del TOKEN. Aceptarlo del cuerpo dejaría nacer una
 *                    cuenta con el correo de otra persona, que es justo la
 *                    dirección a la que el cartero manda todo.
 *   · `phoneNorm`  — derivado. Lo calcula el servidor.
 *   · `idVerified`, `verificationStatus: 'VERIFIED'`, `founder*`, `clientPro*`,
 *     `mechSubscription*`, `suspended`, `emergency*`, `isSuperAdmin`,
 *     `workshopId` — los decide el servidor. Una cuenta no puede nacer
 *     verificada, ni Pro, ni administradora.
 *   · `role: 'ADMIN'` — sólo `CLIENT`, `MECHANIC` y `WORKSHOP_OWNER` se piden
 *     para uno mismo.
 *
 * Por eso las pantallas NO arman el cuerpo: se lo piden a `userCreateBody`,
 * `userProfileBody` e `identityBody`, que traducen los nombres que la app ya usa
 * (`photoURL`, `notificationPrefs`, `honorific: 'ing'`) a los del contrato.
 *
 * ─── NADA SE TRAGA UN FALLO ─────────────────────────────────────────────────
 *
 * Todas las escrituras LANZAN con su tipo y su código. La única que no lo hace
 * hacia arriba es `ensureUserProvisioned`, y lleva su razón escrita en su propio
 * recuadro: no es un guardado del usuario, es aprovisionamiento, y nadie está
 * mirando una pantalla que diga "guardado".
 */
import { apiFetch, ApiError } from './client';

// ────────────────────────────────────────────────────────────────────────────
// TRADUCCIÓN DE NOMBRES Y ENUMS
//
// Postgres habla en MAYÚSCULAS y la app en minúsculas. No es cosmético: un
// `role: 'client'` o un `honorific: 'ing'` enviados tal cual son un 400 que
// tumba el alta entera.
// ────────────────────────────────────────────────────────────────────────────

/** Los tres roles que una cuenta puede pedir para sí. `admin` NO está. */
const ROLE_API = {
  client: 'CLIENT',
  mechanic: 'MECHANIC',
  workshop_owner: 'WORKSHOP_OWNER',
};

const HONORIFIC_API = new Set(['SR', 'SRA', 'SRTA', 'LIC', 'ING', 'DR', 'DRA', 'ARQ', 'PROF', 'NONE']);
const LANGUAGE_API = new Set(['ES', 'EN']);
const GAUGE_API = new Set(['CLUSTER', 'RING', 'BARS']);

/** Edad mínima. La misma que `MIN_AGE_YEARS` en el servicio del API. */
export const MIN_AGE_YEARS = 18;

/** Texto con contenido, o `null`. `''` no es un valor, es un campo vacío. */
function texto(v) {
  const s = typeof v === 'string' ? v.trim() : '';
  return s === '' ? null : s;
}

/** Una URL de verdad, o `undefined` para NO mandar el campo. */
function url(v) {
  const s = texto(v);
  if (!s) return undefined;
  return /^https?:\/\//i.test(s) ? s : undefined;
}

function bool(v) {
  return v === true ? true : v === false ? false : undefined;
}

/** Fecha civil YYYY-MM-DD de alguien mayor de edad, o `null`. */
function fechaCivilAdulta(v) {
  const s = texto(v);
  if (!s || !/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(s)) return null;
  const [y, m, d] = s.split('-').map(Number);
  const limite = Date.UTC(y + MIN_AGE_YEARS, m - 1, d);
  const hoy = new Date();
  const ahora = Date.UTC(hoy.getUTCFullYear(), hoy.getUTCMonth(), hoy.getUTCDate());
  return limite <= ahora ? s : null;
}

function enumDe(v, permitidos) {
  const s = typeof v === 'string' ? v.trim().toUpperCase() : '';
  return permitidos.has(s) ? s : undefined;
}

/** Sólo entra lo que tiene valor. `undefined` = "no mandes ese campo". */
function sinIndefinidos(obj) {
  const out = {};
  for (const [k, v] of Object.entries(obj)) if (v !== undefined) out[k] = v;
  return out;
}

/**
 * EL CUERPO DEL ALTA, a partir del perfil que la app ya tiene.
 *
 * ⚠️ Aquí se OMITE lo dudoso en vez de mandarlo: un `birthDate` con basura o de
 * un menor devolvería 400 y la cuenta no llegaría a existir en Postgres —o sea,
 * ni un carro, ni un pedido, ni una cita para esa persona. En el alta nadie está
 * mirando una pantalla, así que vale más una fila con un campo de menos (que el
 * PATCH del perfil rellena) que ninguna fila.
 */
export function userCreateBody(profile = {}) {
  const p = profile || {};
  return sinIndefinidos({
    role: ROLE_API[String(p.role || p.userType || '').toLowerCase()],
    fullName: texto(p.fullName) || undefined,          // min 1: vacío se omite
    phone: texto(p.phone) || undefined,
    address: texto(p.address) || undefined,
    birthDate: fechaCivilAdulta(p.birthDate) || undefined,
    photoUrl: url(p.photoURL ?? p.photoUrl),
    honorific: enumDe(p.honorific, HONORIFIC_API),
    language: enumDe(p.language, LANGUAGE_API),
    gaugeStyle: enumDe(p.gaugeStyle, GAUGE_API),
    onboardingSeen: bool(p.onboardingSeen),
    honorificAsked: bool(p.honorificAsked),
    profileComplete: bool(p.profileComplete),
    notifyByEmail: bool(p.notificationPrefs?.email),
    notifyByWhatsapp: bool(p.notificationPrefs?.whatsapp),
    notifyByPush: bool(p.notificationPrefs?.push),
  });
}

/**
 * EL CUERPO DE "EDITAR MI PERFIL".
 *
 * Distinto del alta a propósito: aquí el usuario SÍ está mirando, así que un
 * campo vacío viaja como `null` —para poder BORRAR la dirección o la fecha— y lo
 * inválido no se esconde: sube el 400 con el nombre del campo y se le enseña.
 *
 * 🔴 `verificationStatus` sólo admite 'NONE' y 'PENDING'. 'VERIFIED' lo decide el
 * admin, y es la insignia verde: mandarlo es un 400. Si el perfil ya está
 * verificado, el campo NO viaja y el servidor conserva su estado.
 */
export function userProfileBody(data = {}) {
  const d = data || {};
  const estado = String(d.verificationStatus || '').toUpperCase();
  return sinIndefinidos({
    fullName: texto(d.fullName) || undefined,
    honorific: enumDe(d.honorific, HONORIFIC_API),
    phone: texto(d.phone),
    address: texto(d.address),
    birthDate: texto(d.birthDate),
    photoUrl: url(d.photoURL ?? d.photoUrl),
    profileComplete: bool(d.profileComplete),
    verificationStatus: estado === 'NONE' || estado === 'PENDING' ? estado : undefined,
  });
}

/**
 * EL CUERPO DEL PERFIL DE MECÁNICO.
 *
 * 🔴 `rating`, `totalReviews` y `verified` NO viajan, y no por descuido: la lista
 * de mecánicos ORDENA por la nota, así que escribirla es comprarse el primer
 * puesto, y el sello lo pone el admin. El contrato ni los nombra —mandarlos es un
 * 400— así que el bug del 2026-06-02 (editar el perfil borraba la reputación) no
 * puede volver por aquí.
 *
 * `diagnosticFee` está en PESOS en la pantalla y en CENTAVOS en la base.
 */
export function mechanicProfileBody(data = {}) {
  const d = data || {};
  const cuota = d.diagnosticFee;
  const n = cuota === undefined || cuota === null || cuota === '' ? null : Number(cuota);
  return sinIndefinidos({
    workshopName: texto(d.workshopName),
    city: texto(d.city),
    address: texto(d.address),
    description: texto(d.description),
    phone: texto(d.phone),
    specialties: Array.isArray(d.specialties) ? d.specialties.filter(Boolean).map(String) : undefined,
    profilePhoto: url(d.profilePhoto) ?? null,
    schedule: d.schedule && typeof d.schedule === 'object' ? d.schedule : null,
    services: Array.isArray(d.services) ? d.services : null,
    diagnosticFeeCents: Number.isFinite(n) ? Math.round(n * 100) : null,
    lat: Number.isFinite(Number(d.lat)) ? Number(d.lat) : null,
    lng: Number.isFinite(Number(d.lng)) ? Number(d.lng) : null,
  });
}

/** El cuerpo de la cédula. Los nombres cambian: `idPhotoURL` → `idPhotoUrl`. */
export function identityBody(data = {}) {
  const d = data || {};
  return {
    idType: texto(d.idType),
    idNumber: texto(d.idNumber),
    idPhotoUrl: url(d.idPhotoURL ?? d.idPhotoUrl) ?? null,
    idPhotoBackUrl: url(d.idPhotoBackURL ?? d.idPhotoBackUrl) ?? null,
    selfiePhotoUrl: url(d.selfiePhotoURL ?? d.selfiePhotoUrl) ?? null,
  };
}

// ════════════════════════════════════════════════════════════════════════════
// LAS ESCRITURAS
// ════════════════════════════════════════════════════════════════════════════

/** El alta. El uid y el correo salen del token; del cuerpo sólo el perfil. */
export async function createUser(body, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  return call('/users', { method: 'POST', body, signal });
}

/** Editar MI perfil. Nadie edita el de otra persona por esta puerta. */
export async function updateSelf(body, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  return call('/users/me', { method: 'PATCH', body, signal });
}

/**
 * Publicar o actualizar el perfil de mecánico.
 *
 * ⚠️ Escribe sólo las columnas ENVIADAS (INSERT ... ON CONFLICT DO UPDATE), y
 * `verified`, `rating`, `totalReviews` y `responseTime*` ni se nombran en el
 * contrato. Es el bug del 2026-06-02 —editar el perfil borraba la reputación—
 * cerrado por construcción.
 */
export async function upsertMechanicProfile(body, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  return call('/users/me/mechanic-profile', { method: 'PUT', body, signal });
}

/** Subir la cédula. Deja la verificación en PENDING; verificar es del admin. */
export async function putIdentity(body, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  return call('/users/me/identity', { method: 'PUT', body, signal });
}

/**
 * ANOTAR QUE ACABO DE ENTRAR.
 *
 * Equivale al `updateDoc(users/{uid}, { lastLoginAt: serverTimestamp() })` de
 * `Login.js`, y con la misma propiedad: el sello lo pone el SERVIDOR, así que
 * solo avanza aunque el reloj del aparato vaya al revés.
 *
 * 🔴 VA UN CUERPO VACÍO A PROPÓSITO. El servidor no lo mira, pero un POST sin
 * cuerpo sale sin `Content-Length` y la puerta de Google lo rechaza con un
 * **411** en HTML antes de llegar al API (gemelo de `confirmRecord` en
 * `vehicles.js`, donde se comprobó contra producción).
 */
export async function recordLogin({ apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  return call('/users/me/login', { method: 'POST', body: {}, signal });
}

/**
 * RECLAMAR EL NÚMERO DE CLIENTE FUNDADOR.
 *
 * Releva a la callable `claimFounderSpot`, que con `users` en Postgres responde
 * `failed-precondition` — o sea que ningún cliente nuevo recibía número mientras
 * la app le seguía pintando «Cliente Fundador #N».
 *
 * Devuelve `{ founderNumber, justAssigned, full, email }`. `founderNumber: null`
 * NO es un fallo: es «no te tocaba» (no eres cliente) o «se llenó el cupo», y por
 * eso `full` viaja aparte.
 *
 * 🔴 CUERPO VACÍO A PROPÓSITO, igual que `recordLogin`: un POST sin cuerpo sale
 * sin `Content-Length` y la puerta de Google lo rechaza con un 411 en HTML antes
 * de llegar al API.
 */
export async function claimFounderSpot({ apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  return call('/users/me/founder-spot', { method: 'POST', body: {}, signal });
}

/**
 * ARRANCAR LA PRUEBA GRATIS DEL MECÁNICO INDEPENDIENTE.
 *
 * Releva a `startMechanicTrial`, que con `users` en Postgres devuelve
 * `{skipped:'migration'}` sin conceder nada. Idempotente y sin prórroga: volver a
 * llamarla NO alarga la prueba.
 */
export async function startMechanicTrial({ apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  return call('/users/me/mechanic-trial', { method: 'POST', body: {}, signal });
}

/** El teléfono para los avisos push. */
export async function registerDevice(fcmToken, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  return call('/users/me/devices', { method: 'POST', body: { fcmToken }, signal });
}

/** Dar de baja el dispositivo. POST y no DELETE: el token va en el cuerpo. */
export async function revokeDevice(fcmToken, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  return call('/users/me/devices/revoke', { method: 'POST', body: { fcmToken }, signal });
}

export async function addFavorite(mechanicId, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  return call(`/users/me/favorites/${encodeURIComponent(mechanicId)}`, { method: 'PUT', signal });
}

export async function removeFavorite(mechanicId, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  return call(`/users/me/favorites/${encodeURIComponent(mechanicId)}`, { method: 'DELETE', signal });
}

/** Apelar una sanción. Es el único trámite que un suspendido total conserva. */
export async function createAppeal(body, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  return call('/users/me/appeals', { method: 'POST', body, signal });
}

// ────────────────────────────────────────────────────────────────────────────
// EL MENÚ PROPIO
//
// `ROLE_API` no sirve aquí: excluye `admin` a propósito, porque es la lista de
// roles que una cuenta puede PEDIR PARA SÍ. Ordenar los iconos del menú no es
// pedir un rol —el admin ya lo es, y tiene su propio juego de destinos
// (`ADMIN_ITEMS`)— así que este mapa es aparte y sí lo incluye.
// ────────────────────────────────────────────────────────────────────────────

const NAV_ROLE_API = {
  client: 'CLIENT',
  mechanic: 'MECHANIC',
  workshop_owner: 'WORKSHOP_OWNER',
  admin: 'ADMIN',
};

/**
 * Guardar el orden del menú DE UN ROL.
 *
 * El rol viaja en el cuerpo porque forma parte de lo que se guarda: guardar el
 * menú de taller no puede pisar el de cliente. Es el equivalente exacto del
 * dot-path `navConfig.<role>` que se usa contra Firestore.
 */
export async function setNavConfig(role, orderedIds, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  const rol = NAV_ROLE_API[String(role || '').toLowerCase()];
  if (!rol) throw new TypeError(`setNavConfig: rol desconocido "${role}"`);
  return call('/users/me/nav-config', {
    method: 'PUT',
    body: { role: rol, orderedIds: (orderedIds || []).map(String) },
    signal,
  });
}

/** Volver al orden de fábrica. Equivale al `navConfig: null` de Firestore. */
export async function resetNavConfig({ apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  return call('/users/me/nav-config', { method: 'DELETE', signal });
}

/** Favoritos del Marketplace. */
export async function addWishlistItem(productId, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  return call(`/users/me/wishlist/${encodeURIComponent(productId)}`, { method: 'PUT', signal });
}

export async function removeWishlistItem(productId, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  return call(`/users/me/wishlist/${encodeURIComponent(productId)}`, { method: 'DELETE', signal });
}

/** Anuncios guardados de la vitrina de autos. */
export async function addSavedListing(listingId, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  return call(`/users/me/saved-listings/${encodeURIComponent(listingId)}`, { method: 'PUT', signal });
}

export async function removeSavedListing(listingId, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  return call(`/users/me/saved-listings/${encodeURIComponent(listingId)}`, { method: 'DELETE', signal });
}

// ════════════════════════════════════════════════════════════════════════════
// EL ALTA IDEMPOTENTE
//
// ╔══════════════════════════════════════════════════════════════════════════╗
// ║ LLAMARLO DOS VECES NO PUEDE ROMPER NI DUPLICAR                           ║
// ╚══════════════════════════════════════════════════════════════════════════╝
//
// El API responde **409 `conflict`** cuando la cuenta ya existe, y ese 409 es un
// ÉXITO para nosotros: significa exactamente lo que queríamos comprobar. Ahí
// está la idempotencia, y está en el SERVIDOR —dentro de la transacción que
// asigna el número de cuenta—, no en un candado del navegador. El `Set` de aquí
// abajo sólo ahorra viajes.
//
// ⚠️ El fallo de RED no se apunta como hecho a propósito: si no se pudo ni
// preguntar, la próxima sesión tiene que volver a intentarlo. Apuntarlo dejaría
// a la cuenta fuera de Postgres para siempre con la app creyendo que está.
//
// 🔴 NO LANZA HACIA ARRIBA. Es lo contrario de lo que hace el resto de este
// archivo, y la razón es que aquí no hay ningún "guardado" que mentir: nadie
// pidió esto ni está esperando un aviso. Reventar el arranque de sesión porque
// el API está caído sería cambiar una migración a medias por una app caída. El
// fallo se deja en la consola con su motivo, y quien lo note de verdad es la
// primera escritura de otro dominio, que sí falla ruidosamente.
// ════════════════════════════════════════════════════════════════════════════

const yaAprovisionados = new Set();
const enVuelo = new Map();

/** Para las pruebas: el `Set` es de módulo y sobrevive entre casos. */
export function resetProvisionCache() {
  yaAprovisionados.clear();
  enVuelo.clear();
}

/**
 * Deja al usuario dado de alta en Postgres si aún no lo estaba.
 *
 * Devuelve `'created' | 'existing' | 'skipped' | 'failed'` — nunca lanza.
 * `skipped` = no hay API configurado o no hay uid; no es un fallo.
 */
export async function ensureUserProvisioned(profile, { apiFetchImpl } = {}) {
  const uid = texto(profile?.id ?? profile?.uid);
  if (!uid) return 'skipped';
  if (yaAprovisionados.has(uid)) return 'existing';
  if (enVuelo.has(uid)) return enVuelo.get(uid);

  const intento = (async () => {
    try {
      await createUser(userCreateBody(profile), { apiFetchImpl });
      yaAprovisionados.add(uid);
      return 'created';
    } catch (err) {
      // El 409 es la respuesta buena: la cuenta ya estaba.
      if (err instanceof ApiError && (err.status === 409 || err.code === 'conflict')) {
        yaAprovisionados.add(uid);
        return 'existing';
      }
      // Sin API configurado la app funciona igual: es el estado normal mientras
      // la migración no está encendida en este entorno.
      if (err instanceof ApiError && err.code === 'not_configured') return 'skipped';
      // Todo lo demás se cuenta, y NO se apunta como hecho: se reintenta.
      console.warn('[users] no se pudo dar de alta la cuenta en el API:', err?.message || err);
      return 'failed';
    } finally {
      enVuelo.delete(uid);
    }
  })();

  enVuelo.set(uid, intento);
  return intento;
}

// ════════════════════════════════════════════════════════════════════════════
// LA LECTURA — `GET /users/:id/profile`
//
// ╔══════════════════════════════════════════════════════════════════════════╗
// ║  LO QUE SALE DE AQUÍ TIENE QUE SER INDISTINGUIBLE DEL DOCUMENTO DE       ║
// ║  FIRESTORE, PORQUE MEDIA APLICACIÓN LO LEE SIN SABER DE DÓNDE VINO.      ║
// ╚══════════════════════════════════════════════════════════════════════════╝
//
// `useAuth().userProfile` lo consumen los guardas de rol, `SuspensionGate`,
// `LaunchGate`, `TwoFactorGate`, el menú y el asistente. Ninguno pregunta de qué
// base salió: leen `userProfile.role === 'workshop_owner'` y siguen. Así que un
// campo que se pierda o que cambie de forma AQUÍ no sale como un error: sale
// como un taller que aparece en la app de cliente, o como un suspendido que
// navega.
//
// Por eso la traducción no es cosmética. Tres cosas se rompen calladas:
//
//   1. LOS NOMBRES.  `photoUrl` → `photoURL`, `isFounder` → `founder`.
//   2. LAS MAYÚSCULAS.  Postgres dice `CLIENT`, `PENDING`, `CLUSTER`; la app
//      compara contra `'client'`, `'pending'`, `'cluster'`. Un `role: 'CLIENT'`
//      sin bajar no lanza nada: cae al `|| 'client'` de `realRole` y por pura
//      suerte acierta con el cliente... y manda al taller al home del cliente.
//   3. LAS FECHAS.  El API manda ISO; la app llama `x.toDate()`. Se devuelve un
//      objeto con la superficie de un `Timestamp` (gemelo de `ts()` en
//      `vehicles.js`), no un `Date` pelado.
//
// ─── LOS SIETE QUE FALTABAN, Y QUE YA VIAJAN ────────────────────────────────
//
// Hasta ahora `getUserProfile` no proyectaba siete campos que la app SÍ lee, y
// ninguno fallaba ruidosamente: `undefined` se lee como "no hay", y "no hay" es
// el valor PERMISIVO de todos ellos. Ya vienen del API y se traducen abajo:
//
//   · `twoFactorDevices`     → `TwoFactorGate.js:40`. Era el peor: con el
//     interruptor `config/security.twoFactorProviders` encendido y este dominio
//     en Postgres, una lista vacía significa que ningún equipo consta como de
//     confianza y todo mecánico y todo taller entra en un bucle de códigos.
//   · `appointmentAckPending`→ `NoShowAckGate.js:26`.
//   · `suspensionAppeal`     → `App.js:205`. El API manda `pendingAppeals`, una
//     lista de FILAS de `user_appeal`; aquí se despliega a los dos campos con
//     los nombres de siempre (`suspensionAppeal` / `emergencyAppeal`).
//   · `profileComplete`      → `ProfilePage.js:50`. El API ya lo ESCRIBÍA; lo
//     que faltaba era devolverlo.
//   · `navConfig`            → `BottomNav.js:230` vía `configForRole`.
//   · `savedListings`        → `AutoDetail.js:64`.
//   · `wishlist`             → `ProductDetail.js:249`.
//
// ─── 🔴 LO QUE SIGUE SIN VIAJAR ─────────────────────────────────────────────
//
//   · `appointmentAck` (el objeto con `disputed`) → lo lee `AdminUserDetail.js:424`,
//     que NO consume `userProfile`: hace su propio `getDoc` a Firestore. La
//     columna `appointment_ack` existe; se expondrá cuando esa pantalla migre.
//   · `plan` / `clientPlan`  → `BottomNav.js:243`. Campos legacy sin dueño en el
//     modelo nuevo; el riel vivo es `clientProStatus`, que sí se traduce abajo.
//   · `idType`, `idNumber`, `idPhotoURL`, `idPhotoBackURL`, `selfiePhotoURL`
//     → OMITIDOS A PROPÓSITO por el API, que solo dice `hasIdentity`. La cédula
//     tiene su propia puerta (`GET /users/:id/identity`). `EditProfile.js:210`
//     ya lee la subcolección privada y solo cae al documento principal para
//     cuentas viejas sin migrar, así que la pérdida es de ese resto.
//
// ⚠️ NADA de esto se rellena aquí a escondidas leyendo Firestore: eso escondería
// el hueco y crearía el split-brain contra el que avisa `migrationConfig.js`.
// ════════════════════════════════════════════════════════════════════════════

/**
 * Sello de tiempo con la superficie de un `Timestamp` de Firestore.
 *
 * Gemelo deliberado de `ts()` en `api/vehicles.js`. NO se importa de allí porque
 * `users` es el dominio raíz —todo lo demás cuelga de él por clave foránea— y
 * hacerlo depender de `vehicles` invierte esa relación. Si aparece un tercero,
 * sube a `client.js`.
 *
 * Una fecha ilegible es `null`, nunca la época de Unix: un 1 de enero de 1970 es
 * un dato falso y encima se pinta.
 */
function ts(iso) {
  if (iso === null || iso === undefined || iso === '') return null;
  if (typeof iso.toDate === 'function') return iso;
  const ms = iso instanceof Date ? iso.getTime() : Date.parse(iso);
  if (!Number.isFinite(ms)) return null;
  return { toDate: () => new Date(ms), toMillis: () => ms };
}

/** Enum del API → el mismo valor en la forma que compara la app. */
function minus(v) {
  return typeof v === 'string' && v ? v.toLowerCase() : null;
}

/** El camino de vuelta de `ROLE_API`, con el `ADMIN` que no se puede pedir. */
const ROLE_APP = {
  CLIENT: 'client',
  MECHANIC: 'mechanic',
  WORKSHOP_OWNER: 'workshop_owner',
  ADMIN: 'admin',
};

/**
 * El perfil de mecánico.
 *
 * `diagnosticFeeCents` vuelve a PESOS porque así lo pinta la pantalla, y se
 * conserva el nombre en centavos por si alguien necesita el entero exacto.
 * `rating`, `totalReviews` y `verified` SÍ se leen (son de solo lectura); lo que
 * no se puede es escribirlos, y de eso se ocupa `mechanicProfileBody`.
 */
export function adaptMechanicProfile(m) {
  if (!m || typeof m !== 'object') return null;
  const cents = m.diagnosticFeeCents;
  return {
    workshopName: m.workshopName ?? null,
    city: m.city ?? null,
    address: m.address ?? null,
    description: m.description ?? null,
    phone: m.phone ?? null,
    specialties: Array.isArray(m.specialties) ? m.specialties : [],
    profilePhoto: m.profilePhoto ?? null,
    schedule: m.schedule ?? null,
    services: m.services ?? null,
    diagnosticFee: Number.isFinite(Number(cents)) && cents !== null ? Number(cents) / 100 : null,
    diagnosticFeeCents: Number.isFinite(Number(cents)) && cents !== null ? Number(cents) : null,
    lat: m.lat ?? null,
    lng: m.lng ?? null,
    verified: m.verified === true,
    rating: m.rating ?? null,
    totalReviews: Number(m.totalReviews) || 0,
    responseTimeHours: m.responseTimeHours ?? null,
    profileViews: Number(m.profileViews) || 0,
  };
}

/** La suscripción de un producto, o `null` si esa persona no tiene ninguna. */
function subDe(subs, producto) {
  if (!Array.isArray(subs)) return null;
  return subs.find((s) => s && s.product === producto) || null;
}

/** Lista de textos, siempre un array. `.includes()` sobre `undefined` revienta. */
function listaDe(v) {
  return Array.isArray(v) ? v.map(String) : [];
}

/**
 * `navConfig`: el API manda `{ "CLIENT": [...] }` con la clave en el enum de
 * Postgres, y `configForRole(role, navConfig)` la busca por `userProfile.role`,
 * que aquí ya sale en minúsculas.
 *
 * 🔴 Esa discordancia NO lanza: `navConfig['client']` sobre `{CLIENT: [...]}` es
 * `undefined`, `configForRole` devuelve `null` y el menú cae al orden de fábrica
 * — o sea, el usuario ve exactamente lo mismo que si el campo no viajara. Es el
 * fallo silencioso del recuadro de arriba, con otro disfraz.
 */
function mapaDeMenu(v) {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const out = {};
  for (const [rol, ids] of Object.entries(v)) {
    if (Array.isArray(ids)) out[ROLE_APP[rol] || minus(rol)] = ids.map(String);
  }
  return Object.keys(out).length ? out : null;
}

/**
 * Las apelaciones abiertas → los dos campos que la app lee.
 *
 * En Firestore eran dos objetos con formas distintas (`submittedAt` en el de
 * suspensión, `createdAt` en el de emergencia); en Postgres son filas del mismo
 * `user_appeal` distinguidas por `kind`. Se emiten LAS DOS fechas con el mismo
 * valor para no obligar a cada lector a saber de cuál venía.
 *
 * Sin `text` a propósito: el API no lo devuelve en el perfil (es el expediente
 * del admin, no del interesado) y quien lee esto solo pregunta si ya apeló.
 */
const APPEAL_CAMPO = { SUSPENSION: 'suspensionAppeal', EMERGENCY: 'emergencyAppeal' };

function apelacionesAbiertas(lista) {
  const out = {};
  if (!Array.isArray(lista)) return out;
  for (const a of lista) {
    const campo = APPEAL_CAMPO[a?.kind];
    if (!campo || out[campo]) continue;
    const cuando = ts(a.createdAt);
    out[campo] = { status: minus(a.status), submittedAt: cuando, createdAt: cuando };
  }
  return out;
}

/**
 * EL PERFIL, con la forma que la app ya sabe leer.
 *
 * 🔴 LOS `autoRenew` SE QUEDAN `undefined` CUANDO NO HAY SUSCRIPCIÓN, y no es un
 * descuido. `MechanicBilling.js:129` y `ClientProBilling.js:131` usan
 * `!== undefined` como "¿hay algo que gestionar?": mandar `false` porque no hay
 * fila le enseñaría un control de "renovación cancelada" a quien nunca se
 * suscribió, invitándolo a "reactivar" una suscripción que no existe.
 */
export function adaptProfile(row) {
  if (!row || typeof row !== 'object') return null;

  const mech = subDe(row.subscriptions, 'MECHANIC');
  const pro = subDe(row.subscriptions, 'CLIENT_PRO');

  const perfil = {
    id: row.id,
    accountNumber: row.accountNumber ?? null,

    fullName: row.fullName ?? null,
    email: row.email ?? null,
    phone: row.phone ?? null,
    phoneNorm: row.phoneNorm ?? null,
    address: row.address ?? null,
    birthDate: row.birthDate ?? null,
    photoURL: row.photoUrl ?? null,          // el API dice `photoUrl`; la app, `photoURL`
    honorific: minus(row.honorific),
    language: minus(row.language),
    gaugeStyle: minus(row.gaugeStyle),

    role: ROLE_APP[row.role] || minus(row.role) || 'client',

    emailVerified: row.emailVerified === true,
    onboardingSeen: row.onboardingSeen === true,
    honorificAsked: row.honorificAsked === true,
    isSuperAdmin: row.isSuperAdmin === true,

    // Verificación de identidad. El API manda las tres piezas; la app compara
    // `verificationStatus` contra minúsculas (`ProfilePage.js:48`).
    idVerified: row.idVerified === true,
    idRejected: row.idRejected === true,
    idRejectionReason: row.idRejectionReason ?? null,
    verificationStatus: minus(row.verificationStatus),
    hasIdentity: row.hasIdentity === true,

    // Fundador. `isFounder` en la base, `founder` en la app — y de ese booleano
    // depende el regalo de MekaFix Pro (`utils/clientPro.js:29`), o sea el tope
    // de vehículos del fundador. Perderlo lo devuelve al límite de 3.
    founder: row.isFounder === true,
    founderNumber: row.founderNumber ?? null,

    workshopId: row.workshopId ?? null,
    workshopIdStale: row.workshopIdStale === true,

    suspended: minus(row.suspended),
    suspensionReason: row.suspensionReason ?? null,
    emergencyStrikes: Number(row.emergencyStrikes) || 0,
    emergencyWarnings: Number(row.emergencyWarnings) || 0,
    emergencyDisabled: row.emergencyDisabled === true,
    appointmentNoShows: Number(row.appointmentNoShows) || 0,

    notificationPrefs: {
      email: row.notifyByEmail !== false,
      whatsapp: row.notifyByWhatsapp !== false,
      push: row.notifyByPush !== false,
    },

    deviceCount: Number(row.deviceCount) || 0,

    // El aviso de "completa tu perfil" (`ProfilePage.js:50`) y el acuse de la
    // falta a una cita (`NoShowAckGate.js:26`).
    profileComplete: row.profileComplete === true,
    appointmentAckPending: row.appointmentAckPending === true,

    // 🔴 Los equipos de confianza del 2FA. Una lista vacía NO es un fallo (es lo
    // normal), pero un `undefined` sí lo sería: `TwoFactorGate.js:40` hace
    // `Array.isArray(...) && ...includes(deviceId)`, así que aquí siempre array.
    twoFactorDevices: listaDe(row.twoFactorDevices),

    // ╔════════════════════════════════════════════════════════════════════════╗
    // ║ LOS PERMISOS DE STAFF. SIEMPRE UN ARRAY, NUNCA `undefined`.             ║
    // ╚════════════════════════════════════════════════════════════════════════╝
    //
    // Una capacidad es un permiso suelto: quien la tiene sigue siendo `client`
    // y solo se le ha abierto una puerta concreta. El servidor las manda
    // ordenadas y `[]` cuando no hay ninguna.
    //
    // 🔴 El `undefined` sería el fallo caro, no el `[]`: la guarda de la ruta
    // hace `.includes()` sobre esto, y sobre `undefined` eso no deniega — revienta.
    // Y un perfil leído de FIRESTORE no pasa por aquí y por diseño no trae
    // capacidades (§6 del plan de puestos), que es la razón de que el contexto
    // exponga además `perfilDesdeApi`: sin esa bandera, «no le toca» y «no se
    // pudo saber» se ven igual.
    capabilities: listaDe(row.capabilities),

    navConfig: mapaDeMenu(row.navConfig),
    wishlist: listaDe(row.wishlist),
    savedListings: listaDe(row.savedListings),

    createdAt: ts(row.createdAt),
    lastLoginAt: ts(row.lastLoginAt),

    mechanicProfile: adaptMechanicProfile(row.mechanicProfile),

    // `suspensionAppeal` / `emergencyAppeal` sólo aparecen si hay una PENDIENTE,
    // igual que en Firestore, donde el campo no existe hasta que se apela.
    ...apelacionesAbiertas(row.pendingAppeals),
  };

  // La suscripción del MECÁNICO individual, desplegada a los tres campos sueltos
  // que la app lee (`MechanicSetup.js:455`, `MechanicBilling.js:129`).
  if (mech) {
    perfil.mechSubscriptionStatus = minus(mech.status);
    perfil.mechSubscriptionPlan = minus(mech.plan);
    perfil.mechSubscriptionExpiry = ts(mech.expiry);
    perfil.mechSubscriptionAutoRenew = mech.autoRenew === true;
  }

  // MekaFix Pro del cliente. `utils/clientPro.js` mira DOS rieles: el nuevo
  // (`clientProStatus`/`clientProExpiry`) y el legacy, que en Firestore era
  // `subscriptionStatus:'active'` + `subscriptionPlan:'client_pro'`. El API los
  // distingue con `isLegacyRail`, así que cada uno sale por su nombre de siempre
  // y el helper sigue acertando sin tocarlo.
  if (pro) {
    perfil.clientProStatus = minus(pro.status);
    perfil.clientProExpiry = ts(pro.expiry);
    perfil.clientProAutoRenew = pro.autoRenew === true;
    if (pro.isLegacyRail === true) {
      perfil.subscriptionStatus = minus(pro.status);
      perfil.subscriptionPlan = 'client_pro';
    }
  }

  return perfil;
}

/**
 * MI perfil.
 *
 * `me` lo resuelve el API contra el token, así que no hace falta pasarle el uid
 * y no hay forma de pedir por error el de otra persona.
 */
export async function fetchMyProfile({ apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  return adaptProfile(await call('/users/me/profile', { signal }));
}

// ────────────────────────────────────────────────────────────────────────────
// LA FICHA PÚBLICA DE OTRA PERSONA — `GET /users/:id` y `GET /mechanics`
//
// ╔══════════════════════════════════════════════════════════════════════════╗
// ║  ESTO ES LO QUE LEEN `MechanicProfile.js` Y LA PESTAÑA «EQUIPO» DEL      ║
// ║  PERFIL DEL TALLER. NO ES `adaptProfile`, Y LA DIFERENCIA IMPORTA.       ║
// ╚══════════════════════════════════════════════════════════════════════════╝
//
// `adaptProfile` traduce MI cuenta: correo, teléfono, dirección, suscripciones,
// sanciones. Nada de eso viaja en la ficha de un desconocido (ver la
// «PROYECCION PUBLICA» en `api/src/services/users.ts`), así que reutilizar aquel
// adaptador publicaría un objeto lleno de `null` que se lee como «este mecánico
// no tiene teléfono» en vez de «ese dato no es tuyo».
//
// ⚠️ El teléfono que sale aquí es `mechanicProfile.phone`, el que el propio
// mecánico PUBLICA para que lo llamen, no el personal de su cuenta.
// ────────────────────────────────────────────────────────────────────────────

/**
 * Un `PublicProfile` → la forma que `MechanicProfile.js` ya sabe leer.
 *
 * Se emiten las dos grafías de la foto (`photoURL` en la app, `photoUrl` en el
 * API) por lo mismo que el logo del taller: para que la pantalla no tenga que
 * saber de qué base vino la persona.
 *
 * `userType` NO se inventa: es el campo de las cuentas ANTIGUAS de Firestore, y
 * en Postgres no existe. La pantalla ya mira `role` primero.
 */
export function adaptPublicProfile(row) {
  if (!row || typeof row !== 'object' || !row.id) return null;
  const foto = row.photoUrl ?? null;
  return {
    id: String(row.id),
    accountNumber: row.accountNumber ?? null,
    fullName: row.fullName ?? null,
    honorific: minus(row.honorific),
    photoURL: foto,
    photoUrl: foto,
    role: ROLE_APP[row.role] || minus(row.role) || 'client',
    idVerified: row.idVerified === true,
    founder: row.isFounder === true,
    founderNumber: row.founderNumber ?? null,
    // El negocio con vínculo ACTIVO. De él cuelgan la insignia «trabaja en X» y
    // el enlace a la ficha del taller.
    workshopId: row.workshopId ?? null,
    // Sello de tiempo, no texto ISO: quien lo pinte llama a `.toDate()` igual
    // que sobre un Timestamp de Firestore, sin saber de qué base vino.
    createdAt: ts(row.createdAt),
    mechanicProfile: adaptMechanicProfile(row.mechanicProfile),
  };
}

/** La ficha pública de alguien. `null` = no existe, o está dada de baja. */
export async function fetchPublicProfile(id, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  try {
    return adaptPublicProfile(await call(`/users/${encodeURIComponent(id)}`, { signal }));
  } catch (err) {
    if (err && (err.status === 404 || err.code === 'not_found')) return null;
    throw err;                                   // el 403 incluido: dice algo distinto
  }
}

// ────────────────────────────────────────────────────────────────────────────
// LOS FAVORITOS DE MECÁNICO — `GET /users/:id/favorites`
//
// ╔══════════════════════════════════════════════════════════════════════════╗
// ║  ESTE LECTOR EXISTE PARA QUE `utils/favorites.js` PUEDA MIGRAR ENTERO.    ║
// ╚══════════════════════════════════════════════════════════════════════════╝
//
// Sin él sólo se podían migrar las dos ESCRITURAS, y eso rompe la función de
// forma visible: el corazón se guarda en Postgres mientras la pantalla se lo
// pregunta a Firestore, así que el usuario pulsa, no ve nada, y vuelve a pulsar.
//
// ─── LO QUE CAMBIA DE FONDO: EL FAVORITO YA NO GUARDA UNA FOTOCOPIA ─────────
//
// En Firestore cada favorito era un documento con el nombre, la foto y la nota
// COPIADOS el día que se pulsó el corazón — o sea, un mecánico que cambia de
// foto o sube de nota seguía saliendo con los datos viejos para siempre. El API
// guarda sólo el vínculo y devuelve el perfil público VIVO en el mismo viaje.
// Por eso aquí no hay que inventar nada: se traduce el perfil que llega.
//
// ⚠️ EL CONTRATO PAGINA Y `getFavorites` NO. `pageSize` tope 50 (MAX_PAGE_SIZE
// del API), así que se piden páginas hasta agotarlas con un techo duro: quien
// tenga más de `FAVORITES_MAX_PAGES × 50` favoritos verá los primeros. El techo
// existe para que un total corrupto no convierta esta lectura en un bucle de
// llamadas facturables; 200 mecánicos favoritos no es un caso real.
// ────────────────────────────────────────────────────────────────────────────

export const FAVORITES_PAGE_SIZE = 50;
export const FAVORITES_MAX_PAGES = 4;

/**
 * Un `PublicProfile` → la forma que consumen `FavoriteMechanics.js` y `Home.js`.
 *
 * `id` y `mechanicId` salen los dos del mismo uid a propósito: en Firestore el
 * id del documento ERA el uid del mecánico y la tarjeta navega con `mechanicId`
 * mientras la lista usa `id` como `key`. Emitir sólo uno rompería uno de los dos.
 *
 * `rating` se fuerza a NÚMERO: `FavoriteMechanics.js:81` hace `.toFixed(1)`, y
 * de Postgres una nota `numeric` puede llegar como texto. Un `"4.50".toFixed`
 * no es un dato raro, es una pantalla en blanco.
 */
export function adaptFavorite(row) {
  if (!row || typeof row !== 'object' || !row.id) return null;
  const m = adaptMechanicProfile(row.mechanicProfile);
  const nota = Number(m?.rating);
  return {
    id: String(row.id),
    mechanicId: String(row.id),
    // Sin texto de reserva aquí: la tarjeta ya cae a su propia clave i18n cuando
    // no hay nombre, y meter un "Mecánico" en español se la saltaría en inglés.
    name: row.fullName || m?.workshopName || null,
    photo: m?.profilePhoto || row.photoUrl || null,
    rating: Number.isFinite(nota) ? nota : 0,
    workshopName: m?.workshopName || '',
  };
}

/** Todos mis favoritos, ya traducidos. Lanza: una lista vacía sería mentir. */
export async function fetchFavorites({ apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  const out = [];
  for (let page = 1; page <= FAVORITES_MAX_PAGES; page++) {
    const pagina = await call('/users/me/favorites', {
      query: { page, pageSize: FAVORITES_PAGE_SIZE },
      signal,
    });
    const items = Array.isArray(pagina && pagina.items) ? pagina.items : [];
    for (const fila of items) {
      const fav = adaptFavorite(fila);
      if (fav) out.push(fav);
    }
    const totalPaginas = Number(pagina && pagina.totalPages);
    if (!items.length || !Number.isFinite(totalPaginas) || page >= totalPaginas) break;
  }
  return out;
}

// ────────────────────────────────────────────────────────────────────────────
// EL DIRECTORIO DE TÉCNICOS — `GET /mechanics`
//
// ╔══════════════════════════════════════════════════════════════════════════╗
// ║  ESTO ES LO QUE VE EL CLIENTE CUANDO BUSCA A QUIÉN ENTREGARLE SU CARRO.   ║
// ╚══════════════════════════════════════════════════════════════════════════╝
//
// Hoy `MechanicsList.js` y `MechanicsMap.js` descargan la colección `users`
// entera filtrando por rol y se quedan con quien tenga `mechanicProfile`. El
// endpoint hace ese mismo recorte en SQL (`JOIN mechanic_profile`), con
// proyección propia: ni cédula, ni correo, ni teléfono personal.
//
// ─── LO QUE EL CONTRATO **NO** TRAE, Y LO QUE ESO SIGNIFICA ─────────────────
//
//   · `suspended` — no viaja A PROPÓSITO (sería una lista negra consultable), y
//     por eso el filtro del sancionado vive en el WHERE del API. Aquí no hay
//     nada que comprobar: quien llega, llega porque puede aparecer.
//   · `workshopId` — la afiliación no es pública. El directorio de Firestore la
//     usa para repartir la nota del mecánico entre las de su taller; con el API
//     esa nota ya la mantiene el servidor dentro del propio taller.
//   · La suscripción del técnico. Ver el comentario de `MechanicsList.js`.
//
// ⚠️ El contrato PAGINA y el directorio NO: se recorren páginas hasta el tope,
// igual que en `fetchAllWorkshops`, y se DICE si no cupo todo.
// ────────────────────────────────────────────────────────────────────────────

export const MECHANICS_PAGE_SIZE = 50;
export const MECHANICS_MAX_PAGES = 8;

/**
 * Un `PublicProfile` → la forma con la que el directorio ya trabaja.
 *
 * `mechanicProfile` se conserva como sub-objeto (y no aplanado) porque las dos
 * pantallas leen `m.mechanicProfile.rating`, `.lat`, `.specialties`… Aplanarlo
 * aquí obligaría a tocar cada una de esas lecturas por un cambio de base.
 */
export function adaptMechanicListing(row) {
  if (!row || typeof row !== 'object' || !row.id) return null;
  const perfil = adaptMechanicProfile(row.mechanicProfile);
  // Sin perfil publicado no hay a quién enseñar: el API ya lo excluye con el
  // JOIN, y esto solo evita que una respuesta rara se cuele como tarjeta vacía.
  if (!perfil) return null;
  return {
    id: String(row.id),
    _type: 'mechanic',
    fullName: row.fullName || '',
    photoURL: row.photoUrl || '',
    // El gate de verificación lo lee por este nombre (`isVerifiedProvider`).
    idVerified: row.idVerified === true,
    // El API ya lo da promediado en HORAS; Firestore guarda suma y cuenta. La
    // pantalla distingue los dos casos, porque promediar dos veces da otro dato.
    _responseTimeHours: perfil.responseTimeHours,
    mechanicProfile: perfil,
  };
}

export async function fetchMechanics(filtros = {}, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  const pedir = (page) => call('/mechanics', {
    query: { ...filtros, page, pageSize: MECHANICS_PAGE_SIZE },
    signal,
  });
  const crudas = (p) => (Array.isArray(p && p.items) ? p.items : []);

  // 🔴 LAS PÁGINAS 2..N VAN A LA VEZ, Y SIGUEN SIENDO LAS MISMAS PÁGINAS.
  // El techo se aplica ANTES de pedir nada (`Math.min`), así que el número de
  // peticiones es el mismo de siempre: `min(totalPages, MECHANICS_MAX_PAGES)`.
  // Esa cuenta es la mitad del freno del directorio en el servidor (120
  // páginas/hora = 15 cargas x 8 páginas), y el centinela del API la lee de este
  // mismo archivo.
  const primera = await pedir(1);
  const declaradasCrudo = Number(primera && primera.totalPages);
  const declaradas = Number.isFinite(declaradasCrudo) ? declaradasCrudo : 1;
  const paginas = Math.min(declaradas, MECHANICS_MAX_PAGES);

  // Si la primera viene vacía no queda nadie que pedir: el recorrido secuencial
  // también paraba ahí.
  const resto = crudas(primera).length
    ? await Promise.allSettled(
      // `allSettled` y NO `all`, y el motivo NO es que `all` deje rechazos
      // sueltos —no los deja: se suscribe a TODAS las promesas que recibe, se
      // comprobó—. Es que `all` rechaza con lo que falle ANTES EN EL TIEMPO: el
      // mismo corte de red daría un mensaje u otro según qué página conteste
      // primero. Aquí la razón que sube es siempre la de la página MÁS
      // TEMPRANA, o sea un fallo reproducible y reportable. Lo que no cambia:
      // el fallo sube entero y NUNCA se devuelve una lista a medias.
      // Y una lista a medias aquí se lee como «en tu zona no hay nadie».
      Array.from({ length: Math.max(0, paginas - 1) }, (_, i) => pedir(i + 2)),
    )
    : [];
  const fallo = resto.find((r) => r.status === 'rejected');
  if (fallo) throw fallo.reason;

  // En orden de PÁGINA, no de llegada.
  const leidas = [primera, ...resto.map((r) => r.value)];
  // Una página vacía cerraba el recorrido y NO es un recorte: ya no quedan
  // técnicos. Lo que venga detrás tampoco entra, porque tampoco entraba antes.
  const vacia = leidas.findIndex((p) => !crudas(p).length);
  const usadas = vacia === -1 ? leidas : leidas.slice(0, vacia);

  const out = [];
  for (const pagina of usadas) {
    for (const fila of crudas(pagina)) {
      const m = adaptMechanicListing(fila);
      if (m) out.push(m);
    }
  }

  const ultima = leidas[leidas.length - 1];
  const total = Number.isFinite(Number(ultima && ultima.total)) ? Number(ultima.total) : null;

  return {
    mechanics: out,
    total: total === null ? out.length : total,
    truncated: vacia === -1 && paginas < declaradas,
  };
}

/**
 * LA TABLA DE HONOR DE TÉCNICOS — abre SIN SESIÓN.
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ 🔴 NO ES `fetchMechanics` CON OTRO NOMBRE.                               ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 *
 * `/rankings` es pública en la app y `GET /mechanics` exige token: al visitante
 * le salía la tabla vacía. El DIRECTORIO no se abrió —lleva el teléfono
 * publicado, la dirección y las coordenadas de cada técnico— y sigue siendo el
 * lector del directorio y del mapa.
 *
 * Aquí llega una página FIJA de 50 con lo mínimo:
 * `{ id, name, city, photoUrl, rating, totalReviews, verified }`. Sin
 * `mechanicProfile`, así que no vale para pintar una tarjeta de contacto — y
 * eso es a propósito.
 */
export async function fetchMechanicRanking({ apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  const payload = await call('/rankings/mechanics', { signal });
  const filas = payload && payload.items;
  if (!Array.isArray(filas)) {
    throw new ApiError('El API no devolvió el ranking de técnicos.', { code: 'bad_response' });
  }
  return filas
    .filter((r) => r && r.id)
    .map((r) => ({
      id: String(r.id),
      name: r.name || '',
      city: r.city || '',
      photo: r.photoUrl || null,
      rating: Number(r.rating) || 0,
      totalReviews: Number(r.totalReviews) || 0,
      verified: r.verified === true,
    }));
}

// ────────────────────────────────────────────────────────────────────────────
// EL MOTIVO REAL DEL RECHAZO
//
// 🔴 El API contesta 400 NOMBRANDO el campo que no acepta. Tragárselo y decir
// "Error al guardar" deja al usuario adivinando; tragárselo y decir "Perfil
// actualizado" es peor, y tiene antecedentes en este proyecto.
//
// Sólo se le enseña lo que viene del API (`ApiError`, incluidos los fallos de
// red, que también dicen algo útil). Un `TypeError` nuestro NO: disfrazarlo de
// respuesta del servidor manda al usuario a revisar un formulario que estaba
// bien.
//
// ⚠️ Gemelo del de `catalog.js`. Cuando haga falta en un tercer dominio, sube a
// `client.js`, que es quien ya decide qué es un error.
// ────────────────────────────────────────────────────────────────────────────

const MAX_MOTIVO = 240;

export function apiErrorReason(err) {
  if (!err || typeof err.message !== 'string') return null;
  if (err.name !== 'ApiError') return null;
  const msg = err.message.trim();
  if (!msg) return null;
  return msg.length > MAX_MOTIVO ? `${msg.slice(0, MAX_MOTIVO - 1)}…` : msg;
}

// ════════════════════════════════════════════════════════════════════════════
// LA COLA DE KYC Y LAS ESCRITURAS DEL ADMIN
//
// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║ 🔴 ESTE ES EL ÚNICO SITIO DEL FRONT QUE RECIBE UNA CÉDULA EN UNA LISTA    ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
//
// Y llega ANIDADA en `identity` porque así la manda el API, a propósito (ver el
// recuadro de `listKycQueue` en `api/src/services/users.ts`). Aquí se APLANA a
// los nombres que `AdminKyc.js` ya lee (`idNumber`, `idPhotoURL`, …) porque esa
// pantalla es su único consumidor y ya los usa; lo que NO se hace es meter esos
// campos en `adaptProfile` ni en `adaptPublicProfile`, que es como la cédula
// acabó una vez en el documento que lee cualquiera (C-2).
//
// ⚠️ Si algún día otra pantalla necesita algo de esta cola, que se lleve el
// estado de verificación, no la rama `identity`.
// ════════════════════════════════════════════════════════════════════════════

/**
 * Una fila de la cola → la forma que `AdminKyc.js` ya sabe pintar.
 *
 * Los nombres de foto salen en la grafía de la app (`idPhotoURL`, con URL en
 * mayúsculas), que es la de Firestore y la que el JSX referencia hoy. Traducir
 * aquí y no en la pantalla es lo que permite que el JSX sea el MISMO por los dos
 * caminos: un solo árbol que pintar, y por tanto un solo sitio donde se puede
 * romper.
 */
export function adaptKycRow(row) {
  if (!row || typeof row !== 'object' || !row.id) return null;
  const pii = (row.identity && typeof row.identity === 'object') ? row.identity : {};
  return {
    id: String(row.id),
    fullName: texto(row.fullName),
    email: texto(row.email),
    role: ROLE_APP[row.role] || minus(row.role) || 'client',
    idVerified: row.idVerified === true,
    idRejected: row.idRejected === true,
    idRejectionReason: texto(row.idRejectionReason),
    idReviewedAt: ts(row.idReviewedAt),
    idType: texto(pii.idType),
    idNumber: texto(pii.idNumber),
    idPhotoURL: texto(pii.idPhotoUrl),
    idPhotoBackURL: texto(pii.idPhotoBackUrl),
    selfiePhotoURL: texto(pii.selfiePhotoUrl),
  };
}

export const KYC_PAGE_SIZE = 50;
/** Techo duro: un `totalPages` corrupto no puede volverse un bucle facturable. */
export const KYC_MAX_PAGES = 8;

/**
 * LA COLA DE VERIFICACIÓN DE IDENTIDAD. Solo el admin; el API responde 403 si no.
 *
 * `status` viaja en MINÚSCULAS ('pending' | 'verified' | 'rejected' | 'all'):
 * los enums de Postgres van en mayúsculas y la traducción vive en el servidor.
 * Mandar 'PENDING' desde aquí sería un 400, que es justo lo que se quiere frente
 * a la alternativa: un filtro que no casa con nada y una cola vacía que se lee
 * como «no hay trabajo pendiente».
 */
export async function fetchKycQueue(status, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  const out = [];
  for (let page = 1; page <= KYC_MAX_PAGES; page += 1) {
    // eslint-disable-next-line no-await-in-loop
    const payload = await call('/admin/kyc', {
      query: { status, page, pageSize: KYC_PAGE_SIZE },
      signal,
    });
    const filas = payload && payload.items;
    if (!Array.isArray(filas)) {
      throw new ApiError('El API no devolvió la cola de verificación.', { code: 'bad_response' });
    }
    out.push(...filas.map(adaptKycRow).filter(Boolean));
    const paginas = Number(payload.totalPages) || 1;
    if (!filas.length || page >= paginas) break;
  }
  return out;
}

/**
 * 🔴 APROBAR O RECHAZAR UNA CÉDULA. Solo el admin.
 *
 * `idReviewedAt` NO viaja: el servicio escribe `id_reviewed_at = now()` en
 * cuanto ve `idVerified` o `idRejected` (`api/src/services/users.ts`). Mandarlo
 * sería un 400 —`AdminUserSchema` es `.strict()`— y además una fecha de revisión
 * puesta por el navegador no es constancia de nada.
 *
 * ⚠️ El motivo se llama `idRejectionReason`, no `rejectionReason`. Es el nombre
 * que la app escribe Y el que `EditProfile.js` lee para enseñárselo al
 * interesado; el desajuste entre los dos ya dejó una vez ese motivo invisible.
 */
export async function reviewIdentity(uid, { verified, reason }, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  const body = verified
    ? { idVerified: true, idRejected: false }
    : { idVerified: false, idRejected: true, idRejectionReason: texto(reason) };
  return call(`/admin/users/${encodeURIComponent(uid)}`, { method: 'PATCH', body, signal });
}

/**
 * EL NOMBRE DE ALGUIEN, para una bandeja que habla de terceros.
 *
 * Devuelve `null` en vez de lanzar: en la bandeja de soporte y en la de
 * emergencias, no poder resolver un nombre significa enseñar el uid —que es
 * exactamente lo que ya hace el camino de Firestore en su `catch`—, no dejar la
 * pantalla en blanco. Quien no sepa el nombre de un cliente igual tiene que
 * poder cancelar su solicitud de grúa.
 */
export async function fetchDisplayName(uid, { apiFetchImpl, signal } = {}) {
  if (!uid) return null;
  const call = apiFetchImpl || apiFetch;
  try {
    // La ficha COMPLETA y no la pública: hace falta el correo, que es el
    // respaldo cuando la cuenta no tiene nombre puesto.
    const r = await call(`/users/${encodeURIComponent(uid)}/profile`, { signal });
    if (!r || typeof r !== 'object') return null;
    return texto(r.fullName) || texto(r.email) || null;
  } catch {
    return null;
  }
}

// ════════════════════════════════════════════════════════════════════════════
// EL PANEL DEL ADMIN — `GET /admin/users` y las escrituras de la consola
//
// ╔══════════════════════════════════════════════════════════════════════════╗
// ║  LO QUE SALE DE AQUÍ TIENE QUE SER INDISTINGUIBLE DEL DOCUMENTO DE       ║
// ║  FIRESTORE, IGUAL QUE `adaptProfile` — Y POR EL MISMO MOTIVO.            ║
// ╚══════════════════════════════════════════════════════════════════════════╝
//
// `AdminDashboard` decide el rol con `u.role || u.userType || 'client'`, filtra
// por `u.mechanicProfile?.verified`, ordena por `u.createdAt`/`u.lastLoginAt` con
// `.toDate()` y arma el correo masivo sobre `u.email`. Ninguna de esas lecturas
// pregunta de qué base vino la fila. Así que aquí se traduce lo mismo de siempre:
// los NOMBRES (`photoUrl`→`photoURL`, `isFounder`→`founder`), las MAYÚSCULAS
// (`MECHANIC`→`mechanic`, `TOTAL`→`total`) y las FECHAS (ISO → sello con
// `.toDate()`).
//
// 🔴 UN `role: 'MECHANIC'` SIN BAJAR NO DA ERROR: cae al `|| 'client'` de
// `roleOf` y el panel pinta a todos los mecánicos como clientes. Es el fallo
// callado de esta migración, aquí con la insignia de rol.
// ════════════════════════════════════════════════════════════════════════════

/**
 * La suspensión, en las dos formas que el panel compara.
 *
 * `suspended` es un enum-o-`null` de DOS niveles (no un booleano), y las
 * pantallas comparan contra `'total'` y `'partial'` en minúsculas.
 */
export function adaptAdminUser(row) {
  if (!row || typeof row !== 'object' || !row.id) return null;
  const foto = row.photoUrl ?? null;
  // `null` = esa cuenta no publicó perfil de mecánico, y NO es lo mismo que
  // publicarlo sin sello: el panel ofrece «verificar mecánico» sobre lo segundo.
  const mech = row.mechanicVerified === null || row.mechanicVerified === undefined
    ? null
    : { verified: row.mechanicVerified === true, workshopName: row.mechanicWorkshopName ?? null };

  const u = {
    id: String(row.id),
    accountNumber: row.accountNumber ?? null,
    fullName: row.fullName ?? null,
    // 🔴 El correo SOLO llega por la ruta del admin: la ficha pública no lo
    // lleva, y sin él el segmentador del correo masivo se queda sin destinos.
    email: row.email ?? null,
    phone: row.phone ?? null,
    photoURL: foto,
    photoUrl: foto,
    role: ROLE_APP[row.role] || minus(row.role) || 'client',
    isSuperAdmin: row.isSuperAdmin === true,

    createdAt: ts(row.createdAt),
    lastLoginAt: ts(row.lastLoginAt),

    suspended: minus(row.suspended),
    suspensionReason: row.suspensionReason ?? null,

    idVerified: row.idVerified === true,
    idRejected: row.idRejected === true,
    verificationStatus: minus(row.verificationStatus),

    founder: row.isFounder === true,
    founderNumber: row.founderNumber ?? null,

    workshopId: row.workshopId ?? null,

    emergencyStrikes: Number(row.emergencyStrikes) || 0,
    emergencyWarnings: Number(row.emergencyWarnings) || 0,
    emergencyDisabled: row.emergencyDisabled === true,
    appointmentNoShows: Number(row.appointmentNoShows) || 0,
    appointmentAckPending: row.appointmentAckPending === true,
    profileComplete: row.profileComplete === true,

    mechanicProfile: mech,

    clientProStatus: minus(row.clientProStatus),
  };

  // La suscripción del mecánico, desplegada a los dos campos sueltos que el
  // panel lee. Se OMITEN cuando no hay fila, por lo mismo que en `adaptProfile`:
  // un `null` inventado se pinta como «vencida» sobre quien nunca se suscribió.
  if (row.mechSubscriptionStatus) {
    u.mechSubscriptionStatus = minus(row.mechSubscriptionStatus);
    u.mechSubscriptionExpiry = ts(row.mechSubscriptionExpiry);
  }
  return u;
}

// El contrato pagina de 50 en 50 y el panel trabaja con la lista ENTERA: además
// de pintarla, de ella salen los destinatarios del correo masivo, y una muestra
// arbitraria manda correos equivocados («sin actividad» a quien la tiene). Se
// recorren páginas hasta el tope y se DICE si no cupo todo, igual que
// `fetchAllWorkshops`: el techo existe para que un total corrupto no convierta
// esto en un bucle de llamadas facturables.
export const ADMIN_USERS_PAGE_SIZE = 50;
export const ADMIN_USERS_MAX_PAGES = 40;   // 2.000 cuentas

/**
 * TODAS las cuentas, ya traducidas.
 *
 * Devuelve `{ users, total, truncated }`. `truncated` NO se esconde: si el panel
 * dijera «1.200 usuarios» pintando 2.000 sin avisar, el admin creería estar
 * mirando el padrón completo.
 */
export async function fetchAdminUsers(filtros = {}, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  const out = [];
  let total = null;
  let paginas = 1;
  let leidas = 0;

  for (let page = 1; page <= ADMIN_USERS_MAX_PAGES; page += 1) {
    // eslint-disable-next-line no-await-in-loop
    const pagina = await call('/admin/users', {
      query: { ...filtros, page, pageSize: ADMIN_USERS_PAGE_SIZE },
      signal,
    });
    const items = Array.isArray(pagina && pagina.items) ? pagina.items : [];
    for (const fila of items) {
      const u = adaptAdminUser(fila);
      if (u) out.push(u);
    }
    total = Number.isFinite(Number(pagina && pagina.total)) ? Number(pagina.total) : null;
    const totalPaginas = Number(pagina && pagina.totalPages);
    paginas = Number.isFinite(totalPaginas) ? totalPaginas : 1;
    leidas = page;
    if (!items.length || page >= paginas) break;
  }

  return { users: out, total: total === null ? out.length : total, truncated: leidas < paginas };
}

/** La ficha COMPLETA de otra cuenta. Solo el admin: el API lo comprueba. */
export async function fetchUserProfile(id, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  try {
    return adaptProfile(await call(`/users/${encodeURIComponent(id)}/profile`, { signal }));
  } catch (err) {
    if (err && (err.status === 404 || err.code === 'not_found')) return null;
    throw err;                                   // el 403 dice algo distinto
  }
}

/**
 * LA CÉDULA Y SUS FOTOS. Su dueño y el admin, nadie más.
 *
 * Es el equivalente de la subcolección `users/{id}/private/identity`. Los nombres
 * cambian (`idPhotoUrl` → `idPhotoURL`), y se emiten las DOS grafías por lo mismo
 * que la foto de perfil: para que la pantalla no tenga que saber de dónde vino.
 */
export function adaptIdentity(row) {
  if (!row || typeof row !== 'object') return null;
  const frente = row.idPhotoUrl ?? null;
  const dorso = row.idPhotoBackUrl ?? null;
  const selfie = row.selfiePhotoUrl ?? null;
  return {
    idType: row.idType ?? null,
    idNumber: row.idNumber ?? null,
    idPhotoURL: frente,
    idPhotoUrl: frente,
    idPhotoBackURL: dorso,
    idPhotoBackUrl: dorso,
    selfiePhotoURL: selfie,
    selfiePhotoUrl: selfie,
  };
}

/** `null` = esa cuenta no ha subido cédula. No es un fallo. */
export async function fetchIdentity(id, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  try {
    return adaptIdentity(await call(`/users/${encodeURIComponent(id)}/identity`, { signal }));
  } catch (err) {
    if (err && (err.status === 404 || err.code === 'not_found')) return null;
    throw err;
  }
}

// ────────────────────────────────────────────────────────────────────────────
// LAS ESCRITURAS DE LA CONSOLA
//
// 🔴 SANCIONAR Y NOMBRAR ADMINISTRADORES SON DOS PUERTAS DISTINTAS, y no es
// plomería: `PATCH /admin/users/:id` es del admin y `PATCH /admin/users/:id/role`
// es del SUPER admin. Hoy, en Firestore, cualquier admin escribe `role: 'admin'`
// directo; por el camino nuevo recibe un 403 con su motivo. Es un cambio de
// PODER, así que el 403 se deja subir con su mensaje en vez de tragárselo: quien
// pulse el botón tiene que enterarse de por qué no pasó nada.
// ────────────────────────────────────────────────────────────────────────────

const SUSPENSION_API = { total: 'TOTAL', partial: 'PARTIAL' };

/**
 * El cuerpo de suspender / levantar.
 *
 * ⚠️ `suspensionDate` NO viaja: la pone y la borra el servidor. Y
 * `suspensionAppeal: null` tampoco existe aquí — en Postgres una apelación es
 * una FILA de `user_appeal`, así que borrarla es RESOLVERLA
 * (`resolveUserAppeal`), que es otra acción con su propio registro.
 */
export function adminSuspensionBody(nivel, motivo) {
  const n = typeof nivel === 'string' ? SUSPENSION_API[nivel.toLowerCase()] : null;
  return {
    suspended: n || null,
    suspensionReason: n ? (typeof motivo === 'string' && motivo.trim() ? motivo.trim() : null) : null,
  };
}

/** Sancionar, sellar o reiniciar contadores. Del admin — pero NO el rol. */
export async function adminUpdateUser(uid, cambios, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  return call(`/admin/users/${encodeURIComponent(uid)}`, { method: 'PATCH', body: cambios, signal });
}

/**
 * NOMBRAR O DEGRADAR ADMINISTRADORES. Solo el SUPER admin (403 para el resto).
 *
 * El rol viaja en MAYÚSCULAS porque es el enum de Postgres, y `ROLE_API` no sirve
 * aquí: aquel excluye `ADMIN` a propósito, porque es la lista de roles que una
 * cuenta puede pedir PARA SÍ.
 */
export async function adminSetUserRole(uid, cambios, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  return call(`/admin/users/${encodeURIComponent(uid)}/role`, {
    method: 'PATCH',
    body: cambios,
    signal,
  });
}

// ════════════════════════════════════════════════════════════════════════════
// ╔══════════════════════════════════════════════════════════════════════════╗
// ║ LOS PUESTOS DE STAFF — dar, quitar y consultar permisos sueltos          ║
// ╚══════════════════════════════════════════════════════════════════════════╝
//
// Una CAPACIDAD es un permiso suelto. Quien la tiene sigue siendo `client`: no
// es medio administrador, es alguien a quien se le abrió una puerta concreta.
// Así se delega «aprueba los talleres que visito» sin entregar el padrón con los
// correos y teléfonos de todos los clientes.
//
// 🔴 LAS TRES RUTAS SON DEL SUPER ADMIN. Repartir llaves no es parte de ser
// administrador; a un admin corriente le responden 403 con su motivo, y ese
// motivo SE ENSEÑA (ver `apiErrorReason`).
// ════════════════════════════════════════════════════════════════════════════

/**
 * EL VOCABULARIO, espejado de `api/src/types/staff.ts`.
 *
 * ⚠️ Copia a mano, como `clientPro.js` o `migrationConfig.js`. No concede nada:
 * el servidor compara contra SU lista y falla cerrado, así que una capacidad que
 * sobre aquí no abre ninguna puerta y una que falte solo deja de poder
 * concederse desde la pantalla. El riesgo real es el inverso —que esta lista se
 * quede corta y el día que el servidor aprenda un permiso nuevo nadie pueda
 * dárselo a nadie— y por eso vive junto al cliente que la usa.
 */
export const STAFF_CAPABILITIES = [
  'workshops.approve',
  'workshops.moderate',
  'appeals.review',
  'subscriptions.read',
  'subscriptions.write',
];

/**
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║ 🔴 DECLARADAS Y NO CONCEDIDAS. LA PANTALLA NO LAS OFRECE.                 ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 *
 * `subscriptions.write` (dar gracia, activar a mano, revocar) existe en el
 * vocabulario para que ampliar el puesto mañana sea conceder un permiso y no
 * escribir código. Pero el dueño decidió el 2026-09-07 que el encargado de
 * clientes **solo consulta**, así que la interfaz de concesión NO la ofrece al
 * lado de las demás: lo que se ofrece con un botón normal se concede por
 * descuido. Si algún día aparece concedida sin que medie esa decisión, es un
 * error — y por eso la pantalla sí la ENSEÑA y permite quitarla cuando ya está
 * puesta: esconder un permiso concedido es dejarlo sin forma de revocarse.
 */
export const STAFF_CAPABILITIES_WITHHELD = ['subscriptions.write'];

/** Las que la pantalla de concesión ofrece. Ver el recuadro de arriba. */
export const STAFF_CAPABILITIES_GRANTABLE =
  STAFF_CAPABILITIES.filter((c) => !STAFF_CAPABILITIES_WITHHELD.includes(c));

/**
 * Los permisos VIGENTES de una cuenta. Solo el super admin.
 *
 * Devuelve `{ items: [{ capability, grantedAt, grantedBy }] }` tal como lo manda
 * el servidor: no se aplana a un array de nombres porque «quién la dio y cuándo»
 * es la mitad de la pregunta cuando hay que deshacer algo.
 */
export async function fetchUserCapabilities(uid, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  const payload = await call(`/admin/users/${encodeURIComponent(uid)}/capabilities`, { signal });
  return { items: Array.isArray(payload && payload.items) ? payload.items : [] };
}

/** Conceder un permiso suelto. Solo el super admin. */
export async function grantUserCapability(uid, capability, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  return call(`/admin/users/${encodeURIComponent(uid)}/capabilities`, {
    method: 'POST',
    body: { capability },
    signal,
  });
}

/**
 * Quitar un permiso suelto. Solo el super admin.
 *
 * ⚠️ La capacidad va en la RUTA, y lleva punto (`workshops.approve`): se codifica
 * para que no se parta en dos segmentos.
 */
export async function revokeUserCapability(uid, capability, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  return call(
    `/admin/users/${encodeURIComponent(uid)}/capabilities/${encodeURIComponent(capability)}`,
    { method: 'DELETE', signal },
  );
}

/** Dar gracia, activar a mano o revocar la membresía de una PERSONA. */
export async function adminSetSubscription(uid, body, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  return call(`/admin/users/${encodeURIComponent(uid)}/subscription`, {
    method: 'PATCH',
    body,
    signal,
  });
}

// ────────────────────────────────────────────────────────────────────────────
// LAS APELACIONES DE LA PERSONA (no las de una reseña)
//
// En Firestore eran DOS campos embebidos en el usuario (`suspensionAppeal` y
// `emergencyAppeal`); en Postgres son filas de `user_appeal` distinguidas por
// `kind`. El panel las pinta desde el usuario, así que aquí se devuelven con la
// forma de siempre MÁS el `appealId`, que es lo único que el camino nuevo
// necesita para poder marcarlas como revisadas.
// ────────────────────────────────────────────────────────────────────────────

const APPEAL_FIELD = { SUSPENSION: 'suspensionAppeal', EMERGENCY: 'emergencyAppeal' };

export function adaptUserAppeal(row) {
  if (!row || typeof row !== 'object' || !row.id) return null;
  const campo = APPEAL_FIELD[row.kind];
  if (!campo) return null;
  const cuando = ts(row.createdAt);
  return {
    appealId: String(row.id),
    userId: row.userId ?? null,
    // 🔴 EL NOMBRE, Y NO EL CORREO. Decisión del dueño (2026-09-07): la cola de
    // apelaciones es la pantalla de un encargado que NO puede ver los datos de
    // contacto de los clientes, y para decidir sobre una apelación basta con
    // saber de quién es. `null` cuando la cuenta no tiene nombre: quien lo pinte
    // dice «cuenta sin nombre», que es cierto, en vez de caer al identificador.
    userName: row.userName ?? null,
    field: campo,
    text: row.text ?? '',
    status: minus(row.status),
    submittedAt: cuando,
    createdAt: cuando,
  };
}

export const ADMIN_APPEALS_PAGE_SIZE = 50;
export const ADMIN_APPEALS_MAX_PAGES = 20;

/**
 * La cola de apelaciones de cuenta. `status` en MAYÚSCULAS (`PENDING`).
 *
 * Devuelve `{ appeals, total, truncated }` y NO un array pelado, por lo mismo
 * que `fetchAdminUsers`: este lector para a las 20 páginas de 50, y una cola
 * cortada que no lo dice no se lee como «faltan» — se lee como «ya no queda
 * nadie esperando respuesta». En una cola de apelaciones eso es una persona
 * sancionada que nadie va a volver a mirar.
 */
export async function fetchUserAppeals(filtros = {}, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  const out = [];
  let total = null;
  let paginas = 1;
  let leidas = 0;

  for (let page = 1; page <= ADMIN_APPEALS_MAX_PAGES; page += 1) {
    // eslint-disable-next-line no-await-in-loop
    const pagina = await call('/admin/appeals', {
      query: { ...filtros, page, pageSize: ADMIN_APPEALS_PAGE_SIZE },
      signal,
    });
    const items = Array.isArray(pagina && pagina.items) ? pagina.items : [];
    for (const fila of items) {
      const a = adaptUserAppeal(fila);
      if (a) out.push(a);
    }
    const t = Number(pagina && pagina.total);
    if (Number.isFinite(t)) total = t;
    const totalPaginas = Number(pagina && pagina.totalPages);
    leidas = page;
    if (Number.isFinite(totalPaginas)) paginas = totalPaginas;
    if (!items.length || !Number.isFinite(totalPaginas) || page >= totalPaginas) break;
  }
  return {
    appeals: out,
    total: total === null ? out.length : total,
    truncated: leidas < paginas,
  };
}

/** Marcar una apelación como revisada, aprobada o rechazada. */
export async function resolveUserAppeal(appealId, status, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  return call(`/admin/appeals/${encodeURIComponent(appealId)}`, {
    method: 'PATCH',
    body: { status },
    signal,
  });
}

// ════════════════════════════════════════════════════════════════════════════
// QUIÉN ES EL DUEÑO DE ESTE CORREO
//
// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║ LA ÚNICA PUERTA DEL API QUE ACEPTA UN CORREO COMO PREGUNTA                ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
//
// La abre compartir el historial de un carro: una persona sabe el correo de otra,
// no su identificador. El contrato y sus cuatro candados (igualdad exacta, un
// solo resultado, sesión obligatoria y freno de ritmo) están en
// `lookupUserByEmail` de `api/src/services/users.ts`.
//
// 🔴 NO DEVUELVE NINGÚN CORREO, y eso no es una omisión que se pueda "arreglar"
// aquí: quien pregunta ya sabe el que escribió. La identidad de las personas con
// las que un carro YA está compartido viaja por otro sitio —la propia lista de
// comparticiones, que solo ve el dueño— precisamente para no convertir esta ruta
// en una fuente de correos ajenos.
//
// Va por POST porque el correo viaja en el CUERPO: en la URL acabaría en los
// registros del servidor y en el historial del navegador.
// ════════════════════════════════════════════════════════════════════════════

/**
 * `null` = nadie tiene ese correo (o hay más de una cuenta con él, que el API
 * trata igual a propósito). Cualquier otro fallo LANZA: un correo que no se pudo
 * comprobar no es un correo que no existe, y decirle al dueño «esa persona no
 * tiene cuenta» cuando el API está caído es mentirle.
 */
export async function lookupUserByEmail(email, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  try {
    const row = await call('/users/lookup', { method: 'POST', body: { email }, signal });
    if (!row || !row.id) return null;
    return { id: String(row.id), fullName: row.fullName ?? null, photoURL: row.photoUrl ?? null };
  } catch (err) {
    if (err instanceof ApiError && (err.status === 404 || err.code === 'not_found')) return null;
    throw err;
  }
}

/**
 * CANJEAR EL CÓDIGO DE 6 DÍGITOS (verificar el correo, o recordar este equipo).
 *
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║ 🔴 SE GASTABA UN CORREO QUE NO SE PODÍA CANJEAR                           ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 *
 * `sendEmailCode` (la callable) sigue MANDANDO el código y funciona. La que se
 * apagó fue `verifyEmailCode`: con `users` en Postgres se niega a correr —y hace
 * bien, su éxito sería escribir en la base que ya nadie lee— y no dejaba
 * sustituto. El cliente veía el banner en su pantalla de inicio, pedía el código,
 * le llegaba, lo tecleaba bien y recibía un error técnico sin traducir.
 *
 * ── POR QUÉ SE RE-ATA `reason` A LA EXCEPCIÓN ───────────────────────────────
 *
 * `apiFetch` construye su `ApiError` con `error` y `message` y descarta el resto
 * del cuerpo. El servidor manda el motivo en los dos campos justamente para no
 * tener que tocar `client.js`, que es por donde pasan TODAS las peticiones de la
 * app. Aquí se vuelve a poner como `reason`, que es el nombre con el que el modal
 * elige su texto por i18n.
 */
export async function apiVerifyEmailCode({ purpose, code, deviceId } = {}, { fetchImpl, signal } = {}) {
  const call = fetchImpl || apiFetch;
  try {
    return await call('/users/me/email-code/verify', {
      method: 'POST',
      body: { purpose, code, deviceId: deviceId || null },
      signal,
    });
  } catch (err) {
    if (err instanceof ApiError && err.status === 400 && typeof err.message === 'string') {
      err.reason = err.message;
    }
    throw err;
  }
}
